<template>
  <swiper-type-two class="product-gallery" :items="items" @change="onSlideChange">
    <template v-slot:item="{ item }">
      <app-video
        v-if="item.isVideo"
        class="product-gallery__item product-gallery__item--video"
        :is-playing="getIsVideoPlaying(item.index)"
        loop
        muted
        playsinline="playsinline"
      >
        <source :src="item.resourceUrl" type="video/mp4" />
      </app-video>
      <responsive-image
        v-else
        :name="item.resourceName"
        :image-height-to-width-ratio="$options.imageHeightToWidthRatio"
        class="product-gallery__item"
      />
    </template>
  </swiper-type-two>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import SwiperTypeTwo from '@/components/common/app-swiper/SwiperTypeTwo.vue';
import ResponsiveImage from '@/components/common/ResponsiveImage.vue';
import AppVideo from '@/components/common/AppVideo.vue';

import { getSizeValue } from '@/models/core/values';
import {
  getProductOptionsOptionByFormulaIdOrDefault,
  getProductOptionsDefaultOption
} from '@/models/core/ProductOptions';
import {
  isProductPackGenericEyeSerum,
  getProductPackUsage,
  getProductPackSize
} from '@/models/core/ProductPack';

import { PRODUCT_NAMES, SERUM_USAGES } from '@/constants/treatment';
import { types } from '@/store/types';

const IMAGE_HEIGHT_TO_WIDTH_RATIO = 1.46;

const PHOTO = {
  [PRODUCT_NAMES.FACE_SERUM]: {
    [SERUM_USAGES.MORNING_ONLY]: [
      'subpageFaceSerumPackAMImage1',
      'subpageFaceSerumPackImage2',
      'subpageFaceSerumPackImage3',
      'subpageFaceSerumPackAMImage4'
    ],
    [SERUM_USAGES.EVENING_ONLY]: [
      'subpageFaceSerumPackPMImage1',
      'subpageFaceSerumPackImage2',
      'subpageFaceSerumPackImage3',
      'subpageFaceSerumPackPMImage4'
    ],
    [SERUM_USAGES.MORNING_AND_EVENING]: [
      'subpageFaceSerumPackAMPMImage1',
      'subpageFaceSerumPackImage2',
      'subpageFaceSerumPackImage3',
      'subpageFaceSerumPackAMPMImage4'
    ],
    [SERUM_USAGES.ONE_MORNING_ONE_EVENING]: [
      'subpageFaceSerumPackOneAMOnePMImage1',
      'subpageFaceSerumPackImage2',
      'subpageFaceSerumPackImage3',
      'subpageFaceSerumPackOneAMOnePMImage4'
    ]
  },
  [PRODUCT_NAMES.EYE_CONTOUR_SERUM]: {
    [SERUM_USAGES.MORNING_ONLY]: [
      'subpageEyeContourSerumAMImage1',
      'subpageEyeContourSerumImage2',
      'subpageEyeContourSerumImage3',
      'subpageEyeContourSerumImage4'
    ],
    [SERUM_USAGES.EVENING_ONLY]: [
      'subpageEyeContourSerumPMImage1',
      'subpageEyeContourSerumImage2',
      'subpageEyeContourSerumImage3',
      'subpageEyeContourSerumImage4'
    ],
    [SERUM_USAGES.MORNING_AND_EVENING]: [
      'subpageEyeContourSerumAMPMImage1',
      'subpageEyeContourSerumImage2',
      'subpageEyeContourSerumImage3',
      'subpageEyeContourSerumImage4'
    ]
  },
  [PRODUCT_NAMES.NEXULTRA_1]: [
    'subpageNexUltra1Image1',
    'subpageNexUltra1Image2',
    'subpageNexUltra1Image3'
  ],
  [PRODUCT_NAMES.NEXULTRA_2]: [
    'subpageNexUltra2Image1',
    'subpageNexUltra2Image2',
    'subpageNexUltra2Image3'
  ],
  [PRODUCT_NAMES.NEXULTRA_3]: [
    'subpageNexUltra3Image1',
    'subpageNexUltra3Image2',
    'subpageNexUltra3Image3'
  ],
  [PRODUCT_NAMES.NEXULTRA_O]: {
    100: [
      'subpageNexUltraO100Image',
      'subpageNexUltraOImage2',
      'subpageNexUltraOImage3',
      'subpageNexUltraOImage4',
      'subpageNexUltraOImage5',
      'subpageNexUltraOImage6'
    ],
    200: [
      'subpageNexUltraO200Image',
      'subpageNexUltraOImage2',
      'subpageNexUltraOImage3',
      'subpageNexUltraOImage4',
      'subpageNexUltraOImage5',
      'subpageNexUltraOImage6'
    ]
  },
  [PRODUCT_NAMES.SUNSCREEN_SPF]: [
    'subpageNexUltraZImage1',
    'subpageNexUltraZImage2',
    'subpageNexUltraZImage3',
    'subpageNexUltraZImage4'
  ],
  [PRODUCT_NAMES.SUNSCREEN_CRM]: [
    'subpageSunscreenCRMImage1',
    'subpageSunscreenCRMImage2',
    'subpageSunscreenCRMImage3',
    'subpageSunscreenCRMImage4',
    'subpageSunscreenCRMImage5'
  ],
  [PRODUCT_NAMES.UNIVERSKIN_H]: [
    'subpageUniverskinHImage1',
    'subpageUniverskinHImage2',
    'subpageUniverskinHImage3',
    'subpageUniverskinHImage4',
    'subpageUniverskinHImage5',
    'subpageUniverskinHImage6'
  ],
  [PRODUCT_NAMES.UNIVERSKIN_M]: [
    'subpageUniverskinMImage1',
    'subpageUniverskinMImage2',
    'subpageUniverskinMImage3',
    'subpageUniverskinMImage4',
    'subpageUniverskinMImage5'
  ],
  [PRODUCT_NAMES.GEL]: {
    30: [
      'subpageGel30Image',
      'subpageGelImage2',
      'subpageGelImage3',
      'subpageGelImage4',
      'subpageGelImage5'
    ],
    100: [
      'subpageGel100Image',
      'subpageGelImage2',
      'subpageGelImage3',
      'subpageGelImage4',
      'subpageGelImage5'
    ]
  },
  [PRODUCT_NAMES.SUNSCREEN_FLD]: [
    'subpageSunscreenFluidImage1',
    'subpageSunscreenFluidImage2',
    'subpageSunscreenFluidImage3',
    'subpageSunscreenFluidImage4',
    'subpageSunscreenFluidImage5'
  ],
  [PRODUCT_NAMES.NEXULTRA_B]: {
    30: [
      'subpageNexUltraB30Image',
      'subpageNexUltraBImage2',
      'subpageNexUltraBImage3',
      'subpageNexUltraBImage4',
      'subpageNexUltraBImage5',
      'subpageNexUltraBImage6'
    ],
    100: [
      'subpageNexUltraB100Image',
      'subpageNexUltraBImage2',
      'subpageNexUltraBImage3',
      'subpageNexUltraBImage4',
      'subpageNexUltraBImage5',
      'subpageNexUltraBImage6'
    ]
  },
  [PRODUCT_NAMES.NEXULTRA_L]: {
    50: [
      'subpageNexUltraL50Image',
      'subpageNexUltraLImage2',
      'subpageNexUltraLImage3',
      'subpageNexUltraLImage4',
      'subpageNexUltraLImage5'
    ]
  },
 [PRODUCT_NAMES.NEXULTRA_P_LIGHT]: [
    'subpageNexUltraPLight1',
    'subpageNexUltraPLight2',
    'subpageNexUltraPLight3',
    'subpageNexUltraPLight4',
    'subpageNexUltraPLight5',
    'subpageNexUltraPLight6'
  ],
  [PRODUCT_NAMES.NEXULTRA_P_RICH]: [
    'subpageNexUltraPRich1',
    'subpageNexUltraPRich2',
    'subpageNexUltraPRich3',
    'subpageNexUltraPRich4',
    'subpageNexUltraPRich5',
    'subpageNexUltraPRich6'
  ],
  [PRODUCT_NAMES.NEXULTRA_UV]: {
    14: [
      'subpageNexUltraUV1',
      'subpageNexUltraUV2',
      'subpageNexUltraUV3',
      'subpageNexUltraUV4',
      'subpageNexUltraUV5',
      'subpageNexUltraUV6'
    ]
  }
};

export default {
  name: 'ProductGallery',
  components: { SwiperTypeTwo, ResponsiveImage, AppVideo },
  imageHeightToWidthRatio: IMAGE_HEIGHT_TO_WIDTH_RATIO,
  props: {
    productName: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      productVideos: [],
      currentSlideIndex: 0
    };
  },
  computed: {
    ...mapState({
      selectedEyeSerumGenericFormulaId: types.state.SELECTED_EYE_SERUM_GENERIC_FORMULA_ID
    }),
    ...mapGetters({
      allProductOptionsSets: types.getters.ALL_PRODUCT_OPTIONS_SETS
    }),
    product() {
      const productOptions = this.allProductOptionsSets[this.productName];
      const defaultOption = getProductOptionsDefaultOption(productOptions);

      if (isProductPackGenericEyeSerum(defaultOption)) {
        return getProductOptionsOptionByFormulaIdOrDefault(
          this.selectedEyeSerumGenericFormulaId,
          productOptions
        );
      }

      return defaultOption;
    },
    productImages() {
      const sizeKey = this.productSize ? getSizeValue(this.productSize) : null;

      const usage = getProductPackUsage(this.product);
      const names =
        PHOTO[this.productName][sizeKey] ||
        PHOTO[this.productName][usage] ||
        PHOTO[this.productName];

      return names.map(resourceName => ({ resourceName }));
    },
    productSize() {
      return getProductPackSize(this.product);
    },
    items() {
      return [...this.productImages, ...this.productVideos].map((item, index) => ({
        ...item,
        index
      }));
    }
  },
  async created() {
    this.productVideos = await this.getVideos();
  },

  methods: {
    async getVideos() {
      const unboxingVideo = await this.getVideo('unboxing');
      const howToUse = await this.getVideo('howToUse');
      const urls = [unboxingVideo, howToUse].filter(value => !!value);

      return urls.map(resourceUrl => ({ resourceUrl, isVideo: true }));
    },
    async getVideo(videoName) {
      try {
        return (
          await import(
            `@/assets/videos/product-subpage/gallery/${this.productName}/${videoName}.mp4`
          )
        ).default;
      } catch (error) {
        // it's ok if there is no video for some products
      }

      return null;
    },
    onSlideChange(slideIndex) {
      this.currentSlideIndex = slideIndex;
    },
    getIsVideoPlaying(itemIndex) {
      const itemsPerPage = window.matchMedia('(min-width: 600px)').matches ? 2 : 1;
      const distanceToItemFromCurrentSlide = itemIndex - this.currentSlideIndex;

      return distanceToItemFromCurrentSlide < itemsPerPage && distanceToItemFromCurrentSlide >= 0;
    }
  }
};
</script>

<style lang="scss" scoped>
.product-gallery {
  &__item {
    &::v-deep img {
      width: 100%;
    }

    &--video {
      object-fit: cover;
      object-position: center;
      width: 100%;
      height: 100%;
    }
  }
}
</style>
